<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0" align="center">
        <v-col cols="2">
          <in-bond v-model="inBond" @change="refresh"/>
        </v-col>
        <v-col cols="2">
          <date-picker label="As of" v-model="date" date-only hide-details @change="refresh"/>
        </v-col>
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
        <v-col cols="2">
          <inventory-categories-select v-model="categoryId" label="Category" type="Product" :category-list.sync="categories" none-text="All" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="2" v-if="inBond">
          <v-switch label="Bottles only" hide-details class="mt-0 ml-1" v-model="bottlesOnly" @change="refresh()"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Physical inventory worksheet - products', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import {format} from 'date-fns'
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import InBond from '@/components/InBond'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'ReportsInventoryProductsPhysical',
  mixins: [printHtml],
  components: {InBond, InventoryCategoriesSelect},
  data() {
    return {
      inBond: null,
      bottlesOnly: false,
      date: format(new Date(), 'yyyy-MM-dd'),
      siteId: null,
      categoryId: null,
      categories: [],
      products: []
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    lotTracking() {
      return !this.inBond && this.company.settings.lotTrackingForDutyPaid ||
             this.inBond && this.company.settings.lotTrackingForProducts;
    },
    sites() {
      return this.$store.getters.sites(true);
    }
  },
  async created() {
    this.$emit('name', 'Physical inventory worksheet - Products')
    this.refresh();
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsInventoryProductsPhysical($dutyPaid:Boolean!,$bottlesOnly:Boolean!,$date:DateTime!,$siteId:ID,$categoryId:ID) {
                                             productsCount(dutyPaid:$dutyPaid,bottlesOnly:$bottlesOnly,date:$date,siteId:$siteId,categoryId:$categoryId) {
                                               product,site,bottles,cases,lot
                                             }
                                           }`,
                                          {dutyPaid:!this.inBond,bottlesOnly:this.bottlesOnly || !this.inBond,
                                           date:this.date+' 23:59:59',siteId:this.siteId,
                                           categoryId:this.categoryId});
      if (!results.error)
        this.products = results.data.productsCount;
      this.loading = false;
    },

    report() {
    const lines = [
                  `<style>
                    @page {size:portrait}
                    .reports-physical-inventory {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .reports-physical-inventory th {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-physical-inventory .spacer {height:18px}
                    .reports-physical-inventory td {height:36px;padding:0 16px;vertical-align:middle}
                    .reports-physical-inventory .title {font-size:18px;text-transform:uppercase}
                    .reports-physical-inventory .sup-title {font-weight:normal;font-size:22px}
                    .reports-physical-inventory .sub-title {font-weight:normal;margin-bottom:20px;font-size:14px}
                    .reports-physical-inventory .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .reports-physical-inventory .text-left {text-align:left}
                    .reports-physical-inventory .text-right {text-align:right}
                    .reports-physical-inventory .line {border-bottom:1px solid #000}
                  </style>
                  <table class="reports-physical-inventory">
                  <thead>
                    <tr>
                      <th colspan="8">
                        <div class="sup-title">${this.company.name}</div>
                      </th>
                    </tr>
                    <tr>
                      <th colspan="8">
                        <div class="title">Physical inventory worksheet - ${this.inBond?'In-bond':'Duty-paid'} Products</div>
                      </th>
                    </tr>
                    <tr>
                      <th colspan="8">
                        <div class="sub-title">As of ${this.$formatDate(this.date)}${this.sites.length>1 && this.siteId!==null? ` for ${this.sites.find(site => site.id === this.siteId)?.name}`:''}</div>
                      </th>
                    </tr>
                    <tr class="header">
                      <th width="30%" class="text-left">Product</th>
                      ${this.sites.length>1 && this.siteId==null ? '<th width="25%" class="text-left">Site</th>':''}
                      ${this.lotTracking ? '<th width="10%" class="text-left">Lot</th>':''}
                      <th width="15%" class="text-right">Book ${!this.inBond||this.bottlesOnly?'':'cases + '}bottles</th>
                      <th width="5%"></th>
                      <th width="15%" class="text-right">Physical count</th>
                    </tr>
                  </thead>
                  <tbody>`
    ];
    let previousProduct = '';
    for (const product of this.products) {
      if (product.cases === 0 && product.bottles === 0)
        continue;
      lines.push(`<tr>`);
      lines.push(`<td>${product.product !== previousProduct ? product.product : ''}</td>`);
      if (this.sites.length>1 && this.siteId==null)
        lines.push(`<td>${product.site}</td>`);
      if (this.lotTracking)
        lines.push(`<td>${product.lot ? product.lot : ''}</td>`);
      lines.push(`<td class="text-right">`);
      if (this.inBond && !this.bottlesOnly)
        lines.push(`${this.$number(product.cases,0)} + `);
      lines.push(`${this.$number(product.bottles,0)}</td>`);
      lines.push(`<td/>`);
      lines.push(`<td class="line"></td>`);
      lines.push(`</tr>`);
      previousProduct = product.product;
    }
    lines.push(`</tbody>`);
    lines.push(`</table>`);
    return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,`,
                      `"Physical inventory worksheet - ${this.inBond?'In-bond':'Duty-paid'} Products",,,,`,
                      `"As of ${this.$formatDate(this.date)}",,,,`,
                      `"Product","Site",${this.lotTracking?'"Lot",':''}"Book ${!this.inBond||this.bottlesOnly?'':'cases + '}bottles","Physical count"`
                    ];

      let previousProduct = '';
      for (const product of this.products) {
        if (product.cases === 0 && product.bottles === 0)
          continue;
        lines.push(`"${product.product !== previousProduct ? product.product : ''}","${this.sites.length>1 && this.siteId==null ? product.site : ''}",${this.lotTracking ? `"${product.lot ? product.lot : ''}",`:''}"${this.inBond&&!this.bottlesOnly ? this.$number(product.cases,0)+' + ' :''}${this.$number(product.bottles,0)}",`);
        previousProduct = product.product;
      }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Physical inventory worksheet - Products.csv');
    }
  }
}
</script>